import { ModaSofa } from '@dora/backpack';
import * as O from 'fp-ts/Option';
import * as Selectors from '../../../store/selectors';
import { useSelector } from '../../hooks/redux';
import { useFormState } from './useFormState';
import { useFormStateToBuildConfiguration } from './useFormStateToBuildConfiguration';
import { Defaults } from './useSofaConfigure/defaults';
import { initialSofaState } from './useSofaConfigure/initialSofaState';

export * from './useSofaConfigure/defaults';

export const useSofaConfigure = (
    seatsPerEndPieceL: number,
    seatsPerEndPieceR: number,
    seatsPerStraightPiece: number,
    defaults: Defaults,
    placementKind: 'standard' | 'curved',
    // TODO: it'd be best to not pass an Option into this function, but it makes this easier for now
    current: O.Option<ModaSofa.Configuration>,
) => {
    const supportsInterstitials = useSelector(Selectors.ModaSofa.rangeSupportsInterstitials);

    // bail early if we don't know whether we support interstitials yet or not - once all the data
    // is loaded we should have a difinitive answer.
    if (supportsInterstitials === undefined) {
        return;
    }

    const seatSizes = {
        endPieceLeft: seatsPerEndPieceL,
        endPieceRight: seatsPerEndPieceR,
        middlePiece: seatsPerStraightPiece,
    };

    // construct the initial state of the form based on any existing sofa and our sofa settings
    // If a current sofa is set, then these defaults should reflect the current configuration.
    const { defaultShape, defaultSizes, defaultArms, defaultCornerStyle } = initialSofaState(
        current,
        seatSizes,
        defaults,
    );

    // the form state is separate from the actual configuration we end up producing
    const {
        shape,
        cornerStyle,
        seatCounts,
        handleSetSeats,
        setShape,
        setCornerStyle,
        interstitials,
    } = useFormState(
        defaultShape,
        defaultSizes,
        defaultArms,
        defaultCornerStyle,
        placementKind,
        seatSizes,
    );

    const result = useFormStateToBuildConfiguration(
        seatSizes,
        shape,
        {
            straightSeats: seatCounts.straight,
            rightSeats: seatCounts.right,
            leftSeats: seatCounts.left,
            middleSeats: seatCounts.middle,
        },
        cornerStyle,
        {
            left: interstitials.left.count,
            right: interstitials.right.count,
            middle: interstitials.middle.count,
        },
    );

    // the app might not be ready to build a configuration yet
    if (result === undefined) {
        return;
    }

    const { configuration } = result;

    return {
        configuration,
        shape,
        setShape,
        handleSetSeats,
        seatCounts,
        cornerStyle,
        setCornerStyle,
        interstitials,
    };
};

/**
 * NOTE: Interstitial Support
 *
 * Some rules have been decided on regarding the inclusion of interstitials in sofas via the UI.
 * These may change as the UI grows more sophisticated, as they may only apply to the limited, one
 * type of interstitial per sofa, single interstitial per arm approach.
 *
 * The only invalid spots for arm rests are
 *   1. next to a corner piece
 *   2. on the outside of an end piece
 *
 * Not including the end-pieces or corners, this means that the smallest sofa for each shape that can accommodate arm rests are;
 *
 * CORNER - at least 1 seats to left or right (allowing placement between an end and middle piece)
 * U-SHAPE - any amount of middle seats, and 1 left/right seats (allowing placement between two middle pieces or an end and middle piece)
 * STRAIGHT - any amount of seats, which is already the smallest size available (allowing placement between end pieces)
 *
 */
